/**
 * Clinic tickets API — real backend (via axios).
 *
 * CUSTOMER ↔ CLINIC tickets. This is the SECOND, separate customer ticketing
 * stream introduced in backend v6 (the first is platform support — see
 * `supportApi.js`). A customer addresses a ticket to a specific clinic by its
 * 6-digit clinic code; the clinic answers from the provider PWA.
 *
 * Customer-facing routes (backend v6):
 *   GET    /user/clinic-tickets              → list my clinic tickets
 *   POST   /user/clinic-tickets              → open a ticket to a clinic
 *   GET    /user/clinic-tickets/:id          → one of my tickets + thread
 *   POST   /user/clinic-tickets/:id/messages → my reply (author 'user')
 *
 * The clinic side (GET/POST/PATCH /clinic/client-tickets) lives in the
 * provider PWA, not here.
 *
 * Shape notes (serialize_clinic_ticket): tickets carry `domain: "clinic"`,
 * `clinicCode`, an optional bilingual `clinicName` ({ fa, en }), plus the
 * shared ticket fields. Message authors are 'user' (customer) or 'clinic'.
 * As with support tickets, the backend serializes each message timestamp as
 * `at`; we normalize it to `createdAt` at this boundary so the pages and the
 * mock share one shape.
 *
 * All requests go through the shared axios instance (Bearer token, 401
 * refresh, contract error envelope err.response.data.error.{code,...}).
 */

import axiosInstance from './axiosInstance';

/**
 * Normalize one backend message: backend emits `at`, the app reads `createdAt`.
 * @param {{ id: string, author: string, body: string, at?: string, createdAt?: string }} msg
 * @returns {{ id: string, author: string, body: string, createdAt: string }}
 */
function normalizeMessage(msg) {
  return {
    id: msg.id,
    author: msg.author,
    body: msg.body,
    createdAt: msg.createdAt ?? msg.at,
  };
}

/**
 * Normalize a backend clinic ticket so its messages carry `createdAt`.
 * Ticket-level fields already match the frontend (clinicCode / clinicName /
 * relatedType / createdAt / updatedAt / messageCount). Safe on list rows
 * (empty `messages`).
 * @param {object|undefined} ticket
 * @returns {object|undefined}
 */
function normalizeTicket(ticket) {
  if (!ticket) return ticket;
  if (!Array.isArray(ticket.messages)) return ticket;
  return { ...ticket, messages: ticket.messages.map(normalizeMessage) };
}

export async function listTickets() {
  const res = await axiosInstance.get('/user/clinic-tickets');
  const body = res.data;
  return {
    ...body,
    data: { ...body.data, tickets: (body.data?.tickets ?? []).map(normalizeTicket) },
  };
}

export async function getTicket(id) {
  const res = await axiosInstance.get(`/user/clinic-tickets/${id}`);
  const body = res.data;
  return { ...body, data: { ...body.data, ticket: normalizeTicket(body.data?.ticket) } };
}

/**
 * Open a new ticket addressed to a clinic.
 * @param {{
 *   clinicCode: string,
 *   subject: string,
 *   category: 'general'|'service_complaint'|'payment_issue'|'technical'|'account'|'other',
 *   message: string,
 *   relatedType?: 'service_history'|'transaction'|null,
 *   relatedId?: string|null
 * }} input
 */
export async function createTicket(input) {
  const res = await axiosInstance.post('/user/clinic-tickets', input);
  const body = res.data;
  return { ...body, data: { ...body.data, ticket: normalizeTicket(body.data?.ticket) } };
}

export async function addMessage(id, body) {
  const res = await axiosInstance.post(`/user/clinic-tickets/${id}/messages`, { body });
  const payload = res.data;
  return { ...payload, data: { ...payload.data, ticket: normalizeTicket(payload.data?.ticket) } };
}

/**
 * Clinics the customer is allowed to message: those they paid for a service at
 * within the last 3 months. Returns the enveloped list as-is (no message
 * normalization needed).
 * @returns {Promise<{ data: { clinics: Array<{ clinicCode: string, clinicName: object }> }, meta?: object }>}
 */
export async function listEligibleClinics() {
  const res = await axiosInstance.get('/user/clinic-tickets/eligible-clinics');
  return res.data;
}

export const clinicTicketsApi = {
  listTickets,
  getTicket,
  createTicket,
  addMessage,
  listEligibleClinics,
};
export default clinicTicketsApi;
